import { calcNetFutureValueForAccount, calculateTotalContribution } from '@/util/ltcFinancialPlanning';
import { useEffect, useMemo } from 'react';
import { ASSET_OPTIONS } from '../AssetSelectionGrid';

interface UseFundingAllocationCardParams {
  assetType: LtcFinancialPlanningAssetType;
  accountDetail: LtcFinancialPlanningAccountDetail;
  allocation: number;
  yearsToLtc: number;
  contributionMonthCount: number;
  paymentOption: LtcFinancialPlanningPaymentOption;
  fundingMode: LtcFinancialPlanningFundingMode;
  onAllocationChange: (amount: number) => void;
  onFundingModeChange?: (mode: LtcFinancialPlanningFundingMode) => void;
}

function getPaymentPeriodLabel(
  paymentOption: LtcFinancialPlanningPaymentOption,
  contributionMonthCount: number,
): string {
  switch (paymentOption) {
    case '10-Pay':
      return '10 years';
    case 'Monthly':
    case 'Annually':
      return `${Math.round(contributionMonthCount / 12)} years`;
    default:
      return '';
  }
}

export function useFundingAllocationCard({
  assetType,
  accountDetail,
  allocation,
  yearsToLtc,
  contributionMonthCount,
  paymentOption,
  fundingMode,
  onAllocationChange,
  onFundingModeChange,
}: UseFundingAllocationCardParams) {
  // Asset label from options
  const assetLabel = useMemo(
    () => ASSET_OPTIONS.find(a => a.type === assetType)?.label || assetType,
    [assetType],
  );

  // Determine if monthly option should be available
  const showModeToggle = paymentOption !== 'Single Pay' && accountDetail.futureContributions > 0;

  // Slider max based on mode
  const sliderMax =
    fundingMode === 'lumpSum' ? accountDetail.currentValue : accountDetail.futureContributions;

  // Slider step based on mode
  const sliderStep = fundingMode === 'monthly' ? 50 : 100;

  // Auto-correct invalid states
  useEffect(() => {
    // If in monthly mode but no monthly contributions available, switch to lumpSum
    if (
      fundingMode === 'monthly' &&
      accountDetail.futureContributions === 0 &&
      onFundingModeChange
    ) {
      onFundingModeChange('lumpSum');
    }
    // If allocation exceeds the max for current mode, clamp it
    else if (allocation > sliderMax) {
      onAllocationChange(sliderMax);
    }
  }, [
    fundingMode,
    accountDetail.futureContributions,
    allocation,
    sliderMax,
    onFundingModeChange,
    onAllocationChange,
  ]);

  // Calculate total contribution toward policy
  const totalContribution = calculateTotalContribution(
    allocation,
    fundingMode,
    paymentOption,
    contributionMonthCount,
  );

  // Payment period label for display
  const paymentPeriodLabel = getPaymentPeriodLabel(paymentOption, contributionMonthCount);

  // Adjusted account detail to reflect allocation being used
  const adjustedDetail: LtcFinancialPlanningAccountDetail = useMemo(() => {
    if (fundingMode === 'lumpSum') {
      return {
        ...accountDetail,
        currentValue: Math.max(0, accountDetail.currentValue - allocation),
      };
    }
    // For monthly mode, we reduce the future contributions instead
    return {
      ...accountDetail,
      futureContributions: Math.max(0, accountDetail.futureContributions - allocation),
    };
  }, [accountDetail, allocation, fundingMode]);

  // Projected value at LTC
  const projectedValue = calcNetFutureValueForAccount(
    adjustedDetail,
    yearsToLtc,
    contributionMonthCount,
  );

  // ROI calculation
  const roi = useMemo(() => {
    const totalContributed = adjustedDetail.futureContributions * contributionMonthCount;
    const totalInvested = adjustedDetail.currentValue + totalContributed;
    return totalInvested > 0 ? ((projectedValue / totalInvested - 1) * 100).toFixed(0) : '0';
  }, [adjustedDetail, contributionMonthCount, projectedValue]);

  // Remaining values for summary display
  const remainingInAccount = Math.max(0, accountDetail.currentValue - allocation);
  const remainingMonthlyContribution = Math.max(0, accountDetail.futureContributions - allocation);

  // Years to LTC rounded for display
  const yearsToLtcRounded = Math.round(yearsToLtc);

  // Contribution label based on mode
  const contributionTypeLabel = fundingMode === 'monthly' ? 'Monthly contribution' : 'One-time contribution';

  return {
    // Labels
    assetLabel,
    paymentPeriodLabel,
    contributionTypeLabel,

    // Slider config
    sliderMax,
    sliderStep,
    showModeToggle,

    // Calculated values
    totalContribution,
    projectedValue,
    roi,
    yearsToLtcRounded,

    // Summary values
    remainingInAccount,
    remainingMonthlyContribution,
  };
}

